import React, { useEffect, useState } from 'react';
import {
  Sparkles, Loader2, TrendingUp, TrendingDown, Minus, AlertTriangle, Handshake, Zap,
} from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';
import { inr, formatDate } from '../lib/format';
import { toast } from 'sonner';
import { MaterialPicker } from '../components/pickers/MaterialPicker';

const TrendBadge = ({ trend }) => {
  const t = (trend || '').toLowerCase();
  const map = {
    rising: { icon: TrendingUp, cls: 'bg-red-50 text-red-700 border-red-200', label: 'Rising' },
    up: { icon: TrendingUp, cls: 'bg-red-50 text-red-700 border-red-200', label: 'Rising' },
    falling: { icon: TrendingDown, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Falling' },
    down: { icon: TrendingDown, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Falling' },
  };
  const m = map[t] || { icon: Minus, cls: 'bg-slate-100 text-slate-600 border-slate-200', label: trend || 'Stable' };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-display font-bold uppercase tracking-wider border ${m.cls}`} data-testid="rate-trend-badge">
      <Icon size={12} /> {m.label}
    </span>
  );
};

const Bullets = ({ title, items, icon: Icon, accent }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className={`text-[11px] font-display font-bold uppercase tracking-widest mb-1.5 flex items-center gap-1.5 ${accent}`}>
        <Icon size={12} /> {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-[13px] text-slate-700 leading-snug flex gap-2">
            <span className="text-slate-300 shrink-0">▪</span><span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const RateIntelligence = () => {
  const [material, setMaterial] = useState(null);
  const [hist, setHist] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [topPurchased, setTopPurchased] = useState([]);

  useEffect(() => {
    apiGet('/procurement/rate-history/top-purchased', { limit: 8 })
      .then((d) => setTopPurchased(d?.items || []))
      .catch(() => setTopPurchased([]));
  }, []);

  const onSelect = async (m) => {
    setMaterial(m);
    setHist(null);
    setAnalysis(null);
    if (!m?.id) return;
    setLoadingHist(true);
    try {
      const d = await apiGet('/procurement/rate-history', { material_id: m.id });
      setHist(d);
    } catch {
      setHist({ items: [], count: 0 });
    } finally { setLoadingHist(false); }
  };

  const runAnalysis = async () => {
    if (!material?.id) return;
    setAnalysing(true);
    try {
      const d = await apiPost(`/ai/procurement/rate-analysis?material_id=${encodeURIComponent(material.id)}`);
      setAnalysis(d);
      toast.success('Rate analysis generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate rate analysis');
    } finally { setAnalysing(false); }
  };

  const items = hist?.items || [];

  return (
    <div data-testid="rate-intelligence-panel">
      <p className="text-xs text-slate-500 mb-4">
        Pick a material to see its PO rate history across vendors, then run an AI negotiation analysis
        (price trend, anomalous quotes, and specific leverage points).
      </p>

      <div className="max-w-md mb-5">
        <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 mb-1">Material</div>
        <MaterialPicker value={material?.id} displayName={material?.name} onSelect={onSelect} testid="rate-intel-material" />
      </div>

      {/* Round 4 P8 — most-purchased quick picks */}
      {topPurchased.length > 0 && (
        <div className="mb-5" data-testid="rate-top-purchased">
          <div className="flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest text-slate-400 mb-2">
            <Zap size={11} /> Frequently ordered — jump to
          </div>
          <div className="flex flex-wrap gap-1.5">
            {topPurchased.map((tp) => (
              <button
                key={tp.material_id}
                type="button"
                onClick={() => onSelect({ id: tp.material_id, name: tp.material_name })}
                data-testid={`rate-top-pick-${tp.material_id}`}
                className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${
                  material?.id === tp.material_id
                    ? 'bg-[#3b4872] text-white border-[#3b4872]'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-[#3b4872] hover:text-[#3b4872]'
                }`}
                title={`${tp.purchases} PO lines · avg ${inr(tp.avg_rate)} / ${tp.uom || '—'}`}
              >
                {tp.material_name} <span className="text-[10px] opacity-70">· {tp.purchases}×</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {material?.id && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Rate history */}
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="rate-history-card">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-400">Rate History</div>
                <h3 className="font-display font-bold text-base leading-tight">{material.name}</h3>
              </div>
              {hist && (
                <div className="text-right">
                  <div className="font-display font-extrabold text-lg tabular-nums">{inr(hist.avg_rate || 0)}</div>
                  <div className="mt-0.5"><TrendBadge trend={hist.price_trend} /></div>
                </div>
              )}
            </div>
            <div className="p-0 max-h-80 overflow-y-auto">
              {loadingHist ? (
                <div className="p-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
              ) : items.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-xs" data-testid="rate-history-empty">
                  No PO rate history for this material yet. Rates are captured automatically as POs are raised.
                </div>
              ) : (
                <table className="w-full dense-table">
                  <thead><tr><th>Date</th><th>Vendor</th><th className="text-right">Rate</th><th>UoM</th></tr></thead>
                  <tbody>
                    {items.map((r, i) => (
                      <tr key={i} data-testid={`rate-history-row-${i}`}>
                        <td className="text-xs text-slate-500">{r.po_date ? formatDate(r.po_date) : '—'}</td>
                        <td className="text-[13px]">{r.vendor_name || '—'}</td>
                        <td className="text-right tabular-nums font-display font-semibold">{inr(r.unit_rate || 0)}</td>
                        <td className="text-xs text-slate-500">{r.uom || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* AI Analysis */}
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="rate-ai-card">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-sm bg-[#3b4872]/10 text-[#3b4872] flex items-center justify-center"><Sparkles size={15} /></span>
                <div>
                  <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-400">AI · Ellora AI</div>
                  <h3 className="font-display font-bold text-base leading-tight">Rate Analysis</h3>
                </div>
              </div>
              <button onClick={runAnalysis} disabled={analysing || items.length === 0} data-testid="rate-ai-generate"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5c] disabled:opacity-50 transition-colors">
                {analysing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {analysing ? 'Analysing…' : analysis ? 'Re-run' : 'Analyse'}
              </button>
            </div>
            <div className="p-4">
              {!analysis ? (
                <p className="text-sm text-slate-400">
                  {items.length === 0
                    ? 'No rate history yet — analysis needs at least one recorded PO rate.'
                    : 'Run an AI analysis to surface the price trend, anomalous quotes, and negotiation leverage.'}
                </p>
              ) : (
                <div className="space-y-4" data-testid="rate-ai-result">
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-400">Price trend</span>
                    <TrendBadge trend={analysis.price_trend} />
                    <span className="text-xs text-slate-400 ml-auto">{analysis.sample_size} quotes · avg {inr(analysis.avg_rate || 0)}</span>
                  </div>
                  <Bullets title="Anomalies" items={analysis.anomalies} icon={AlertTriangle} accent="text-red-600" />
                  <Bullets title="Negotiation leverage" items={analysis.negotiation_leverage} icon={Handshake} accent="text-[#3b4872]" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RateIntelligence;
